import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { useCompanyTier } from '@/hooks/use-company-tier';
import { getRequiredTier, hasTierAccess, getTierDisplayName } from '@/lib/tier-permissions';

interface TierGateProps {
  children: ReactNode;
}

export function TierGate({ children }: TierGateProps) {
  const { pathname } = useLocation();
  const { tier, isPlatformAdmin, loading } = useCompanyTier();

  const requiredTier = getRequiredTier(pathname);

  // Platform admins bypass all tier restrictions
  if (!loading && isPlatformAdmin) {
    return <>{children}</>;
  }

  // Loading state — show spinner inside Layout so nav stays visible
  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  // Access granted — render page
  if (hasTierAccess(tier, requiredTier)) {
    return <>{children}</>;
  }

  // Blocked — render glassmorphic lock overlay inside Layout
  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="glass-panel-strong p-8 max-w-md w-full text-center space-y-6 rounded-2xl border border-white/10">
          {/* Lock icon */}
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto backdrop-blur-sm">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>

          {/* Title */}
          <h2 className="text-h2 font-bold">Erişim Kısıtlı</h2>

          {/* Message */}
          <p className="text-muted-foreground leading-relaxed">
            Bu sayfayı mevcut abonelik seviyeniz nedeniyle kullanamazsınız.
            {tier === 'free' && (
              <>
                {' '}Free hesaplar sadece Ne Ödeyeceğim ve e-İrsaliye sayfalarını kullanabilir.
              </>
            )}
            Bir hata olduğunu düşünüyorsanız veya yükseltmek için{' '}
            <a
              href="mailto:hello@idatech.online"
              className="text-accent hover:underline font-medium"
            >
              hello@idatech.online
            </a>{' '}
            ile iletişime geçebilirsiniz.
          </p>

          {/* Current tier badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
            <span className="text-sm text-muted-foreground">Mevcut paket:</span>
            <span className="text-sm font-semibold">{getTierDisplayName(tier)}</span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
